import clsx from "clsx";
import React from "react";
import {
  DialogProps,
  Stack,
  Button,
  Box,
  CircularProgress,
  TextField,
  Divider,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { BaseDialog } from "../dialog/BaseDialog";

import { RecipientList, RecipientListProps } from "./RecipientList";
import MessagesService from "../../services/MessagesService";
import { ThreadSkeleton } from "./ThreadSkeleton";
import useInfiniteScrollQuery from "../../hooks/useInfiniteScroll";
import { useDebounce } from "../../hooks/useDebounce";

const useStyles = makeStyles((theme) => ({
  root: {},
  paper: {
    width: 524,
    maxHeight: "90vh",
  },
  list: {
    height: 350,
    overflowY: "auto",
  },
  button: {
    height: theme.spacing(7),
    fontSize: 16,
    fontWeight: "bold",
    lineHeight: "20px",
    marginTop: theme.spacing(2),
  },
  loaderWrapper: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2, 0),
  },
}));

const RECIPIENT_DIALOG_PAGE_SIZE = 10;

export interface RecipientDialogProps
  extends Omit<DialogProps, "onSelect">,
    Pick<RecipientListProps, "onSelect"> {}

export function RecipientDialog(props: RecipientDialogProps) {
  const s = useStyles();
  const { onSelect, className, ...other } = props;
  const [filter, setFilter] = React.useState("");
  const debouncedFilter = useDebounce(filter, 300);
  const [selectedRecipient, setSelectedRecipient] = React.useState<any>(null);

  const handleFilterChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFilter(event.target.value);
    },
    [],
  );

  const {
    data: recipientPages,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    ref: infiniteScrollRef,
  } = useInfiniteScrollQuery({
    queryKey: ["get-coach-recipients", debouncedFilter],
    queryFn: ({ pageParam = 1 }) =>
      MessagesService.getRecipients(
        pageParam as number,
        RECIPIENT_DIALOG_PAGE_SIZE,
        debouncedFilter ? debouncedFilter : null,
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage?.hasNextPage ? pages.length + 1 : undefined,
  });

  const recipients = recipientPages?.pages.flatMap((page) => page.items) ?? [];

  React.useEffect(() => {
    const selectedInRecipients = recipients.find(
      (r) => r.user?.username === selectedRecipient?.user?.username,
    );
    if (!selectedInRecipients) {
      setSelectedRecipient(null);
    }
  }, [recipients, selectedRecipient]);

  const handleSelect = React.useCallback(
    (_: any, username?: string) => {
      const alreadySelected = selectedRecipient?.user?.username === username;

      if (alreadySelected) {
        setSelectedRecipient(null);
        return;
      }

      const foundRecipient = recipients.find(
        (foundRecipient) => foundRecipient.user?.username === username,
      );
      setSelectedRecipient(foundRecipient || null);
    },
    [recipients],
  );

  return (
    <BaseDialog
      className={clsx(s.root, className)}
      classes={{
        paper: s.paper,
      }}
      title="New message"
      {...other}
    >
      <TextField
        variant="outlined"
        fullWidth
        value={filter}
        onChange={handleFilterChange}
        label="Search recipients"
      />

      <Box className={s.list}>
        {isLoading ? (
          <Stack spacing={2} sx={{ p: 1, mt: 1 }}>
            {Array.from({ length: 3 }).map((_, index) => (
              <ThreadSkeleton key={index} />
            ))}
          </Stack>
        ) : (
          <>
            <RecipientList
              recipients={recipients}
              hasNext={!!hasNextPage}
              loading={isFetchingNextPage}
              onLoadMore={fetchNextPage}
              onSelect={handleSelect}
              selectedRecipient={selectedRecipient}
              singleSelect
              debounceQuery={debouncedFilter}
            />
            {hasNextPage && (
              <div ref={infiniteScrollRef} style={{ height: 10 }} />
            )}

            {isFetchingNextPage && (
              <div className={s.loaderWrapper}>
                <CircularProgress size={24} />
              </div>
            )}
          </>
        )}
      </Box>

      <Divider />
      <Button
        className={s.button}
        variant="contained"
        fullWidth
        children="Next"
        disabled={!selectedRecipient}
        onClick={() =>
          selectedRecipient &&
          onSelect?.(null as any, selectedRecipient.user?.username)
        }
      />
    </BaseDialog>
  );
}
